import { motion } from "framer-motion";

export function GlowBackdrop({ intense = false }) {
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <div className="absolute -top-40 -left-32 h-[520px] w-[520px] rounded-full bg-cyan-400/10 blur-[140px] animate-pulse-glow" />
      <div className="absolute top-1/3 -right-32 h-[600px] w-[600px] rounded-full bg-violet-500/10 blur-[150px] animate-float-slow" />
      <div className="absolute bottom-0 left-1/4 h-[420px] w-[420px] rounded-full bg-blue-500/10 blur-[120px] animate-pulse-glow" />
      {intense && <div className="absolute inset-0 hero-grid" />}
    </div>
  );
}

export function PageWrap({ children, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16, filter: "blur(6px)" }}
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function GlassCard({ className = "", children, hover = true, ...rest }) {
  return (
    <div
      className={`glass relative overflow-hidden ${hover ? "transition-all duration-300 hover:-translate-y-0.5 hover:border-white/15 hover:shadow-[0_18px_60px_rgba(0,240,255,0.08)]" : ""} ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}

export function SectionLabel({ children }) {
  return <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500 font-mono">{children}</div>;
}

export function GradientButton({ as: As = "button", className = "", children, ...rest }) {
  return (
    <As
      className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 font-semibold text-white
        bg-gradient-to-r from-[#3B82F6] to-[#9D4CDD] hover:from-[#00F0FF] hover:to-[#3B82F6]
        shadow-[0_0_24px_rgba(59,130,246,0.35)] hover:shadow-[0_0_36px_rgba(0,240,255,0.45)]
        transition-all duration-300 ${className}`}
      {...rest}
    >
      {children}
    </As>
  );
}

export function GhostButton({ as: As = "button", className = "", children, ...rest }) {
  return (
    <As
      className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 font-medium text-slate-200
        bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/20 transition ${className}`}
      {...rest}
    >
      {children}
    </As>
  );
}

export function SeverityBadge({ value = "medium" }) {
  const map = {
    critical: "bg-red-500/10 text-red-400 border-red-500/30",
    high: "bg-orange-500/10 text-orange-400 border-orange-500/30",
    medium: "bg-blue-500/10 text-blue-400 border-blue-500/30",
    low: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  };
  return (
    <span className={`px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-wider border ${map[value] || map.medium}`}>
      {value}
    </span>
  );
}

export function StatusBadge({ value = "open" }) {
  const map = {
    open: "bg-amber-500/10 text-amber-400 border-amber-500/30",
    investigating: "bg-cyan-500/10 text-cyan-300 border-cyan-500/30",
    mitigated: "bg-blue-500/10 text-blue-300 border-blue-500/30",
    resolved: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  };
  return (
    <span className={`px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-wider border ${map[value] || map.open}`}>
      {value}
    </span>
  );
}

export function ConfidenceBar({ value = 0.85 }) {
  const pct = Math.round(value * 100);
  return (
    <div className="w-full">
      <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">
        <span>AI Confidence</span>
        <span className="text-cyan-300">{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="h-full rounded-full bg-gradient-to-r from-cyan-400 via-blue-500 to-violet-500"
        />
      </div>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-20 px-6">
      {Icon && <div className="mb-4 h-14 w-14 rounded-2xl glass flex items-center justify-center"><Icon className="h-7 w-7 text-cyan-300" /></div>}
      <h3 className="text-xl font-semibold">{title}</h3>
      {description && <p className="text-sm text-slate-400 mt-1 max-w-md">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}
